import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "maly_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (e) { void e; }
  }, [items]);

  const addItem = (product, { size, color, quantity = 1 } = {}) => {
    setItems((prev) => {
      const key = `${product.id}__${size || ""}__${color || ""}`;
      const idx = prev.findIndex((it) => it.key === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          image: product.images?.[0],
          price: product.price,
          currency: product.currency || "EUR",
          size,
          color,
          quantity,
        },
      ];
    });
    setIsOpen(true);
  };

  const updateQty = (key, qty) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, quantity: Math.max(1, qty) } : it)));
  };
  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));
  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + it.price * it.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, it) => s + it.quantity, 0), [items]);

  const value = { items, addItem, updateQty, removeItem, clear, subtotal, count, isOpen, setIsOpen };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
